import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { AppLayout } from '@/components/layout/AppLayout';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { LoadingBlock } from '@/components/ui';
import LoginPage from '@/pages/auth/LoginPage';
import PasswortVergessenPage from '@/pages/auth/PasswortVergessenPage';
import PasswortZuruecksetzenPage from '@/pages/auth/PasswortZuruecksetzenPage';

/**
 * Routing – deutsche Pfade. Seiten werden lazy geladen (schneller Start auch auf Mobilgeräten).
 */
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const MietuebersichtPage = lazy(() => import('@/pages/finance/MietuebersichtPage'));
const HeatmapPage = lazy(() => import('@/pages/finance/HeatmapPage'));
const RueckstaendePage = lazy(() => import('@/pages/finance/RueckstaendePage'));
const ZahlungenPage = lazy(() => import('@/pages/finance/ZahlungenPage'));
const BankPage = lazy(() => import('@/pages/bank/BankPage'));
const BankKontenPage = lazy(() => import('@/pages/bank/BankKontenPage'));
const BankRegelnPage = lazy(() => import('@/pages/bank/BankRegelnPage'));
const MahnwesenPage = lazy(() => import('@/pages/dunning/MahnwesenPage'));
const MieterListPage = lazy(() => import('@/pages/tenants/MieterListPage'));
const MieterFormPage = lazy(() => import('@/pages/tenants/MieterFormPage'));
const MieterAktePage = lazy(() => import('@/pages/tenants/MieterAktePage'));
const MietverhaeltnisListPage = lazy(() => import('@/pages/tenancies/MietverhaeltnisListPage'));
const MietverhaeltnisNeuPage = lazy(() => import('@/pages/tenancies/MietverhaeltnisNeuPage'));
const MietverhaeltnisDetailPage = lazy(() => import('@/pages/tenancies/MietverhaeltnisDetailPage'));
const ImmobilienListPage = lazy(() => import('@/pages/properties/ImmobilienListPage'));
const ImmobilieDetailPage = lazy(() => import('@/pages/properties/ImmobilieDetailPage'));
const EinheitenPage = lazy(() => import('@/pages/units/EinheitenPage'));
const EinheitDetailPage = lazy(() => import('@/pages/units/EinheitDetailPage'));
const EigentuemerPage = lazy(() => import('@/pages/owners/EigentuemerPage'));
const EigentuemerDetailPage = lazy(() => import('@/pages/owners/EigentuemerDetailPage'));
const KautionenPage = lazy(() => import('@/pages/deposits/KautionenPage'));
const ZaehlerPage = lazy(() => import('@/pages/meters/ZaehlerPage'));
const SchluesselPage = lazy(() => import('@/pages/keys/SchluesselPage'));
const NebenkostenPage = lazy(() => import('@/pages/utilities/NebenkostenPage'));
const AbrechnungDetailPage = lazy(() => import('@/pages/utilities/AbrechnungDetailPage'));
const PostausgangPage = lazy(() => import('@/pages/communication/PostausgangPage'));
const UnterschreibenPage = lazy(() => import('@/pages/auth/UnterschreibenPage'));
const AusgabenPage = lazy(() => import('@/pages/expenses/AusgabenPage'));
const AufgabenPage = lazy(() => import('@/pages/tasks/AufgabenPage'));
const WiedervorlagenPage = lazy(() => import('@/pages/tasks/WiedervorlagenPage'));
const SchaedenPage = lazy(() => import('@/pages/tickets/SchaedenPage'));
const SchadenDetailPage = lazy(() => import('@/pages/tickets/SchadenDetailPage'));
const HandwerkerPage = lazy(() => import('@/pages/contractors/HandwerkerPage'));
const HandwerkerDetailPage = lazy(() => import('@/pages/contractors/HandwerkerDetailPage'));
const DokumentePage = lazy(() => import('@/pages/documents/DokumentePage'));
const KommunikationPage = lazy(() => import('@/pages/communication/KommunikationPage'));
const BerichtePage = lazy(() => import('@/pages/reports/BerichtePage'));
const FinanzenPage = lazy(() => import('@/pages/reports/FinanzenPage'));
const ImportPage = lazy(() => import('@/pages/imports/ImportPage'));
const KiAssistentPage = lazy(() => import('@/pages/ai/KiAssistentPage'));
const AssistentPage = lazy(() => import('@/pages/assistant/AssistentPage'));
const BenachrichtigungenPage = lazy(() => import('@/pages/notifications/BenachrichtigungenPage'));
const EinstellungenPage = lazy(() => import('@/pages/settings/EinstellungenPage'));
const AuditPage = lazy(() => import('@/pages/audit/AuditPage'));
const ProfilPage = lazy(() => import('@/pages/profile/ProfilPage'));
const PortalPage = lazy(() => import('@/pages/portal/PortalPage'));
const HandwerkerPortalPage = lazy(() => import('@/pages/handwerker-portal/HandwerkerPortalPage'));
const AuftragPage = lazy(() => import('@/pages/auth/AuftragPage'));
const AuftraegePage = lazy(() => import('@/pages/auftraege/AuftraegePage'));
const WartungPage = lazy(() => import('@/pages/wartung/WartungPage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingBlock text="Anmeldung wird geprüft …" className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  // Mieter (Portal-Benutzer) haben ausschließlich Zugriff auf das Mieterportal
  if (user.role.key === 'MIETER' && !location.pathname.startsWith('/portal')) return <Navigate to="/portal" replace />;
  // Handwerker (Portal-Benutzer) haben ausschließlich Zugriff auf das Handwerkerportal
  if (user.role.key === 'HANDWERKER' && !location.pathname.startsWith('/handwerkerportal')) return <Navigate to="/handwerkerportal" replace />;
  return <>{children}</>;
}

function Lazy({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingBlock />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/passwort-vergessen" element={<PasswortVergessenPage />} />
      <Route path="/passwort-zuruecksetzen" element={<PasswortZuruecksetzenPage />} />
      <Route path="/unterschreiben" element={<Lazy><UnterschreibenPage /></Lazy>} />
      <Route path="/auftrag" element={<Lazy><AuftragPage /></Lazy>} />
      <Route
        path="/handwerkerportal/*"
        element={
          <RequireAuth>
            <Lazy>
              <HandwerkerPortalPage />
            </Lazy>
          </RequireAuth>
        }
      />
      <Route
        path="/portal/*"
        element={
          <RequireAuth>
            <Lazy>
              <PortalPage />
            </Lazy>
          </RequireAuth>
        }
      />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Lazy><DashboardPage /></Lazy>} />
        <Route path="mietuebersicht" element={<Lazy><MietuebersichtPage /></Lazy>} />
        <Route path="heatmap" element={<Lazy><HeatmapPage /></Lazy>} />
        <Route path="rueckstaende" element={<Lazy><RueckstaendePage /></Lazy>} />
        <Route path="zahlungen" element={<Lazy><ZahlungenPage /></Lazy>} />
        <Route path="bank" element={<Lazy><BankPage /></Lazy>} />
        <Route path="bank/konten" element={<Lazy><BankKontenPage /></Lazy>} />
        <Route path="bank/regeln" element={<Lazy><BankRegelnPage /></Lazy>} />
        <Route path="mahnwesen" element={<Lazy><MahnwesenPage /></Lazy>} />
        <Route path="mieter" element={<Lazy><MieterListPage /></Lazy>} />
        <Route path="mieter/neu" element={<Lazy><MieterFormPage /></Lazy>} />
        <Route path="mieter/:id/bearbeiten" element={<Lazy><MieterFormPage /></Lazy>} />
        <Route path="mieter/:id" element={<Lazy><MieterAktePage /></Lazy>} />
        <Route path="mietverhaeltnisse" element={<Lazy><MietverhaeltnisListPage /></Lazy>} />
        <Route path="mietverhaeltnisse/neu" element={<Lazy><MietverhaeltnisNeuPage /></Lazy>} />
        <Route path="mietverhaeltnisse/:id" element={<Lazy><MietverhaeltnisDetailPage /></Lazy>} />
        <Route path="immobilien" element={<Lazy><ImmobilienListPage /></Lazy>} />
        <Route path="immobilien/:id" element={<Lazy><ImmobilieDetailPage /></Lazy>} />
        <Route path="einheiten" element={<Lazy><EinheitenPage /></Lazy>} />
        <Route path="einheiten/:id" element={<Lazy><EinheitDetailPage /></Lazy>} />
        <Route path="leerstand" element={<Navigate to="/einheiten?tab=leerstand" replace />} />
        <Route path="eigentuemer" element={<Lazy><EigentuemerPage /></Lazy>} />
        <Route path="eigentuemer/:id" element={<Lazy><EigentuemerDetailPage /></Lazy>} />
        <Route path="kautionen" element={<Lazy><KautionenPage /></Lazy>} />
        <Route path="zaehler" element={<Lazy><ZaehlerPage /></Lazy>} />
        <Route path="schluessel" element={<Lazy><SchluesselPage /></Lazy>} />
        <Route path="nebenkosten" element={<Lazy><NebenkostenPage /></Lazy>} />
        <Route path="nebenkosten/abrechnungen/:id" element={<Lazy><AbrechnungDetailPage /></Lazy>} />
        <Route path="postausgang" element={<Lazy><PostausgangPage /></Lazy>} />
        <Route path="ausgaben" element={<Lazy><AusgabenPage /></Lazy>} />
        <Route path="aufgaben" element={<Lazy><AufgabenPage /></Lazy>} />
        <Route path="wiedervorlagen" element={<Lazy><WiedervorlagenPage /></Lazy>} />
        <Route path="schaeden" element={<Lazy><SchaedenPage /></Lazy>} />
        <Route path="schaeden/:id" element={<Lazy><SchadenDetailPage /></Lazy>} />
        <Route path="auftraege" element={<Lazy><AuftraegePage /></Lazy>} />
        <Route path="wartung" element={<Lazy><WartungPage /></Lazy>} />
        <Route path="handwerker" element={<Lazy><HandwerkerPage /></Lazy>} />
        <Route path="handwerker/:id" element={<Lazy><HandwerkerDetailPage /></Lazy>} />
        <Route path="dokumente" element={<Lazy><DokumentePage /></Lazy>} />
        <Route path="kommunikation" element={<Lazy><KommunikationPage /></Lazy>} />
        <Route path="berichte" element={<Lazy><BerichtePage /></Lazy>} />
        <Route path="finanzen" element={<Lazy><FinanzenPage /></Lazy>} />
        <Route path="import" element={<Lazy><ImportPage /></Lazy>} />
        <Route path="ki" element={<Lazy><KiAssistentPage /></Lazy>} />
        <Route path="assistent" element={<Lazy><AssistentPage /></Lazy>} />
        <Route path="benachrichtigungen" element={<Lazy><BenachrichtigungenPage /></Lazy>} />
        <Route path="einstellungen/*" element={<Lazy><EinstellungenPage /></Lazy>} />
        <Route path="audit" element={<Lazy><AuditPage /></Lazy>} />
        <Route path="profil" element={<Lazy><ProfilPage /></Lazy>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
